import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {ContentTypeModel, JsonFileAssignmentType, JsonFileType} from "@generated"
import {
    resolvedTextureRevisionSchema,
    resolvedTextureSetRevisionSchema,
    type ResolvedTextureRevision,
    type ResolvedTextureSetRevision,
} from "@cm/material-nodes"
import {graphToJson} from "@cm/utils/graph-json"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {Settings} from "@common/models/settings/settings"
import {
    MaterialForMapsExportGQL,
    MaterialMapsExporterCreateJobGQL,
    MaterialMapsExporterCreateJsonFileAssignmentGQL,
    MaterialMapsExporterJsonFileAssignmentsGQL,
    MaterialRevisionForMapsExportGQL,
    NormalToDisplacementStepTextureRevisionGQL,
} from "@common/services/material-maps-exporter/material-maps-exporter.generated"
import {NameAssetFromSchemaService} from "@common/services/name-asset-from-schema/name-asset-from-schema.service"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"
import {type DataObject} from "@cm/utils"
import {z} from "zod"
import {createBrowserLogger} from "@cm/browser-log"
import {MaterialMapsExporterCore, PbrExport, PconExport} from "@cm/material-maps-exporter"

type LegacyId = MaterialMapsExporterCore.Types.LegacyId
type IdDetails = MaterialMapsExporterCore.Types.IdDetails
type MaterialDetails = MaterialMapsExporterCore.Types.MaterialDetails
type SourceInfo = MaterialMapsExporterCore.Types.SourceInfo
type TextureRevisionInfo = MaterialMapsExporterCore.Types.TextureRevisionInfo

const MaterialDetailsSchema = z.object({
    id: z.string(),
    legacyId: z.number(),
    articleId: z.string().nullable().optional(),
    name: z.string(),
    organization: z.object({
        legacyId: z.number(),
        name: z.string(),
    }),
    tagAssignments: z.array(z.object({tag: z.object({name: z.string()})})),
})

type PbrExportConfigDBEntry = {config: PbrExport.Types.Config; jsonObjectId: string; jsonObjectLegacyId: number}

export type PbrExportSummary = {[K in "workflow" | "engine" | "format" | "normalY" | "resolution"]: Array<PbrExport.Types.ConversionConfig[K]>}

export type PbrExportQueryResult = PbrExportConfigDBEntry & {
    summary: PbrExportSummary
    dataObjectId: number | undefined
    updateAvailable: boolean
}

@Injectable({
    providedIn: "root",
})
export class MaterialMapsExporterService implements MaterialMapsExporterCore.DataSource.MapsExporterDataSource {
    private readonly nameAssetFromSchemaService = inject(NameAssetFromSchemaService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)

    private readonly materialMapsExporterJsonFileAssignments = inject(MaterialMapsExporterJsonFileAssignmentsGQL)
    private readonly materialMapsExporterCreateJsonFileAssignment = inject(MaterialMapsExporterCreateJsonFileAssignmentGQL)
    private readonly materialMapsExporterCreateJob = inject(MaterialMapsExporterCreateJobGQL)
    private readonly materialForMapsExport = inject(MaterialForMapsExportGQL)
    private readonly materialRevisionForMapsExport = inject(MaterialRevisionForMapsExportGQL)
    private readonly normalToDisplacementStepTextureRevision = inject(NormalToDisplacementStepTextureRevisionGQL)
    private readonly dataResolverService = inject(DataResolverService)

    constructor() {
        MaterialMapsExporterCore.Types.setMaterialMapsExporterLogger(inject(CM_LOGGER_MATERIALS))
    }

    // MapsExporterDataSource implementation

    async resolveTextureRevision(legacyId: number): Promise<ResolvedTextureRevision> {
        const raw = await this.dataResolverService.resolve.textureRevisionLegacy(legacyId)
        const parsed = resolvedTextureRevisionSchema.safeParse(raw)
        if (!parsed.success) throw Error("Invalid resolved texture revision data")
        return parsed.data
    }

    async resolveDataObject(id: string): Promise<DataObject> {
        const raw = await this.dataResolverService.resolve.dataObject(id)
        const parsed = z.object({width: z.number(), height: z.number()}).safeParse(raw)
        if (!parsed.success) throw Error("Invalid resolved image data object")
        return raw
    }

    async resolveTextureSetRevision(id: string): Promise<ResolvedTextureSetRevision> {
        const raw = await this.dataResolverService.resolve.textureSetRevision(id)
        const parsed = resolvedTextureSetRevisionSchema.safeParse(raw)
        if (!parsed.success) throw Error("Invalid resolved texture set revision data")
        return parsed.data
    }

    async fetchMaterialRevision(materialId: {legacyId: LegacyId} | {id: string}) {
        const rev = (await fetchThrowingErrors(this.materialRevisionForMapsExport)(materialId)).material.latestCyclesRevision
        if (!rev) throw Error("Material does not have a cycles revision")
        return rev
    }

    async fetchNormalToDisplacementRevision(legacyId: number): Promise<MaterialMapsExporterCore.DataSource.NormalToDisplacementData> {
        return (await fetchThrowingErrors(this.normalToDisplacementStepTextureRevision)({legacyId}))
            .textureRevision as MaterialMapsExporterCore.DataSource.NormalToDisplacementData
    }

    // Pbr exports

    private async fetchPbrExportConfigsDB(materialIdDetail: IdDetails): Promise<PbrExportConfigDBEntry[]> {
        const filter = "id" in materialIdDetail ? {objectId: materialIdDetail["id"]} : {objectLegacyId: materialIdDetail["legacyId"]}
        const assignments = (
            await fetchThrowingErrors(this.materialMapsExporterJsonFileAssignments)({
                filter: {
                    ...filter,
                    contentTypeModel: ContentTypeModel.Material,
                },
            })
        ).jsonFileAssignments

        return assignments
            .filter((assignment) => assignment.assignmentType === JsonFileAssignmentType.MaterialMapsExportConfig)
            .map((assignment) => ({
                config: assignment.jsonFile.content,
                jsonObjectLegacyId: assignment.jsonFile.legacyId,
                jsonObjectId: assignment.jsonFile.id,
            }))
    }

    private async storePbrExportConfigDB(materialDetails: MaterialDetails, exportConfig: PbrExport.Types.Config): Promise<PbrExportConfigDBEntry> {
        const result = await mutateThrowingErrors(this.materialMapsExporterCreateJsonFileAssignment)({
            input: {
                jsonFile: {
                    content: exportConfig,
                    organizationLegacyId: materialDetails.organization.legacyId,
                    type: JsonFileType.MaterialMapsExportConfig,
                },
                objectLegacyId: materialDetails.legacyId,
                contentTypeModel: ContentTypeModel.Material,
                type: JsonFileAssignmentType.MaterialMapsExportConfig,
            },
        })

        const config = result.createJsonFileAssignment?.jsonFile?.content
        const jsonFileLegacyId = result.createJsonFileAssignment?.jsonFile?.legacyId
        const jsonFileId = result.createJsonFileAssignment?.jsonFile?.id
        if (!config || !jsonFileId) throw Error("Failed to create export config")

        return {
            config,
            jsonObjectId: jsonFileId,
            jsonObjectLegacyId: jsonFileLegacyId,
        }
    }

    async queryPbrExportsForMaterial(materialIdDetails: IdDetails): Promise<PbrExportQueryResult[]> {
        const matchSourceInfos = (sourceInfo1: SourceInfo, sourceInfo2: SourceInfo) => {
            if (sourceInfo1.source !== sourceInfo2.source) return false
            if (sourceInfo1.source === "textureSet") return sourceInfo1.sourceId === sourceInfo2.sourceId
            else if (sourceInfo1.source === "materialRevision") return true
            else throw Error(`Unknown source info sources: ${sourceInfo1.source}/${sourceInfo2.source}`)
        }

        const textureRevisionInfoEqual = (a: TextureRevisionInfo, b: TextureRevisionInfo) => {
            if (a.textureType !== b.textureType) return false
            if (a.revisionId !== b.revisionId) return false
            return true
        }

        const sourceInfosEqual = (a: SourceInfo, b: SourceInfo) => {
            if (a.source !== b.source) return false
            if (a.sourceId !== b.sourceId) return false
            if (a.source === "textureSet") {
                if (a.textureRevisionInfo === undefined || b.textureRevisionInfo === undefined) throw Error("Texture revision info is undefined")

                if (a.textureRevisionInfo.length !== b.textureRevisionInfo.length) return false

                return (
                    a.textureRevisionInfo.every(
                        (aTextureRevisionInfo) =>
                            b.textureRevisionInfo!.find((bTextureRevisionInfo) => textureRevisionInfoEqual(aTextureRevisionInfo, bTextureRevisionInfo)) !==
                            undefined,
                    ) &&
                    b.textureRevisionInfo.every(
                        (bTextureRevisionInfo) =>
                            a.textureRevisionInfo!.find((aTextureRevisionInfo) => textureRevisionInfoEqual(aTextureRevisionInfo, bTextureRevisionInfo)) !==
                            undefined,
                    )
                )
            }
            return true
        }

        const resultForConfigDBEntry = (configDBEntry: PbrExportConfigDBEntry, updateAvailable: boolean): PbrExportQueryResult => {
            return {
                ...configDBEntry,
                summary: this.getSummaryForPbrExportConfigOrPbrExportRequest(configDBEntry.config),
                updateAvailable,
                dataObjectId:
                    configDBEntry.config.state === "done" && configDBEntry.config.output !== undefined ? configDBEntry.config.output.dataObjectId : undefined,
            }
        }

        const configsDB = await this.fetchPbrExportConfigsDB(materialIdDetails)

        const resultsForTextureSetBasedConfigs = configsDB
            .filter((configDBEntry) => configDBEntry.config.sourceInfo.source === "textureSet")
            .map((configDBEntry) => resultForConfigDBEntry(configDBEntry, false))

        const resultsForMaterialRevisionBasedConfigs = await (async () => {
            const filteredConfigsDB = configsDB.filter((configDBEntry) => configDBEntry.config.sourceInfo.source === "materialRevision")
            const sourceInfos = filteredConfigsDB.map((configDBEntry) => configDBEntry.config.sourceInfo)
            const uniqueSourceInfos = sourceInfos.filter((el, i) => !sourceInfos.some((el2, i2) => i < i2 && matchSourceInfos(el, el2)))
            const populatedSourceInfos = await Promise.all(
                uniqueSourceInfos.map(async (sourceInfo) => {
                    try {
                        const {sourceInfo: populatedSourceInfo} = await PbrExport.Gather.gatherMaterialDataForPbrExport(this, materialIdDetails, sourceInfo)
                        return populatedSourceInfo
                    } catch (_error: unknown) {
                        return sourceInfo
                    }
                }),
            )
            return filteredConfigsDB.map((configDBEntry) => {
                const oldSourceInfo = configDBEntry.config.sourceInfo

                const populatedSourceInfo = populatedSourceInfos.find((populatedSourceInfo) => matchSourceInfos(populatedSourceInfo, oldSourceInfo))
                if (populatedSourceInfo === undefined) throw Error("populatedSourceInfo === undefined")

                const updateAvailable = !sourceInfosEqual(populatedSourceInfo, oldSourceInfo)
                return resultForConfigDBEntry(configDBEntry, updateAvailable)
            })
        })()

        return [...resultsForTextureSetBasedConfigs, ...resultsForMaterialRevisionBasedConfigs]
    }

    async generatePbrExport(exportRequest: PbrExport.Types.Request, materialIdDetails: IdDetails): Promise<{jobId: string; configJsonObjectId: string}> {
        const {mapsInfo, sourceInfo} = await PbrExport.Gather.gatherMaterialDataForPbrExport(this, materialIdDetails, exportRequest.sourceInfoRequest)

        const queryResult = (await fetchThrowingErrors(this.materialForMapsExport)(materialIdDetails)).material
        const parsedQueryResult = MaterialDetailsSchema.safeParse(queryResult)
        if (!parsedQueryResult.success) throw Error(`Failed to query all material details`)
        const materialDetails = parsedQueryResult.data

        const generateConvertedMapFilename = (
            materialDetails: MaterialDetails,
            conversionRequest: PbrExport.Types.ConversionRequest,
            mapType: PbrExport.Types.TextureType,
        ) => this.nameAssetFromSchemaService.getMaterialMapsExportMapName(materialDetails.id, conversionRequest, mapType)

        const generateConvertedInfoFilename = (materialDetails: MaterialDetails, conversionInfoRequest: PbrExport.Types.ConversionInfoRequest) =>
            this.nameAssetFromSchemaService.getMaterialMapsExportInfoName(materialDetails.id, conversionInfoRequest)

        const config = await PbrExport.Config.exportConfigForExportRequest(
            exportRequest,
            materialDetails,
            sourceInfo,
            mapsInfo,
            generateConvertedMapFilename,
            generateConvertedInfoFilename,
        )

        const zipFilename = await this.nameAssetFromSchemaService.getMaterialMapsExportName(materialDetails.id, config)
        const jobGraphFn = PbrExport.JobGraph.jobGraphFnForPbrExportConfig(config, mapsInfo, materialDetails, Settings.APP_VERSION, zipFilename)
        const addedConfig = await this.storePbrExportConfigDB(materialDetails, config)

        const jobName = `PBR export generation: Material ${materialDetails.legacyId}`
        const result = await mutateThrowingErrors(this.materialMapsExporterCreateJob)({
            input: {
                name: jobName,
                organizationLegacyId: materialDetails.organization?.legacyId,
                graph: graphToJson(jobGraphFn(addedConfig.jsonObjectLegacyId), createBrowserLogger("materials")),
            },
        })

        if (!result.createJob?.id) throw new Error(`Failed to create job`)
        return {jobId: result.createJob.id, configJsonObjectId: addedConfig.jsonObjectId}
    }

    getSummaryForPbrExportConfigOrPbrExportRequest(_export: PbrExport.Types.Config | PbrExport.Types.Request): PbrExportSummary {
        const summary: PbrExportSummary = {
            workflow: [],
            engine: [],
            format: [],
            normalY: [],
            resolution: [],
        }

        const traverse = (folder: (typeof _export)["root"]) => {
            folder.content.map((item) => {
                switch (item.type) {
                    case "exportFolder":
                        traverse(item)
                        break
                    case "conversionRequest":
                    case "conversionConfig":
                        summary.workflow.push(
                            item.workflow ? item.workflow : item.engine === "vray" || item.engine === "corona" ? "specularGlossiness" : "metalnessRoughness",
                        )
                        summary.engine.push(item.engine ? item.engine : "cm")
                        summary.format.push(item.format ? item.format : "exr")
                        summary.normalY.push(item.normalY ? item.normalY : "default")
                        summary.resolution.push(item.resolution ? item.resolution : "original")
                        break
                }
            })
        }
        traverse(_export.root)

        summary.workflow = Array.from(new Set(summary.workflow)).sort()
        summary.engine = Array.from(new Set(summary.engine)).sort()
        summary.format = Array.from(new Set(summary.format)).sort()
        summary.normalY = Array.from(new Set(summary.normalY)).sort()
        summary.resolution = Array.from(new Set(summary.resolution)).sort()
        return summary
    }

    // pCon exports

    async generatePconExport(materialIdDetails: IdDetails): Promise<{jobId: string}> {
        const queryResult = (await fetchThrowingErrors(this.materialForMapsExport)(materialIdDetails)).material
        const parsedQueryResult = MaterialDetailsSchema.safeParse(queryResult)
        if (!parsedQueryResult.success) throw Error(`Failed to query all material details`)
        const materialDetails = parsedQueryResult.data

        const {mapsInfo, commonWidthCm, commonHeightCm} = await PconExport.Gather.gatherMaterialDataForPconExport(this, materialIdDetails)
        const exportConfig = PconExport.Types.getDefaultPconExportConfig({commonWidthCm, commonHeightCm})
        const jobGraph = PconExport.JobGraph.jobGraphForPconExport(materialDetails, mapsInfo, exportConfig, Settings.APP_VERSION, materialDetails.name)

        const jobName = `pCon export generation: Material ${materialDetails.legacyId}`
        const result = await mutateThrowingErrors(this.materialMapsExporterCreateJob)({
            input: {
                name: jobName,
                organizationLegacyId: materialDetails.organization?.legacyId,
                graph: graphToJson(jobGraph, createBrowserLogger("materials")),
            },
        })

        if (!result.createJob?.id) throw new Error(`Failed to create job`)
        return {jobId: result.createJob.id}
    }
}
